import { FormGroup, Input } from '@starsoft/common/components';
import { useFormContext } from 'react-hook-form';
import styles from '../../styles.module.scss';
import { UserPaymentDetailsFormContentFieldsProps } from '../props';
import {
  CreateUserPaymentDetailsInputDto,
  UpdateUserPaymentDetailsInputDto,
} from '@starsoft/common/dto';
import { UserPaymentDetailsFormFields } from '@starsoft/common/enums';
import {
  useCoallescedUserPaymentTypeId,
  useTranslation,
} from '../../../../../../../../../hooks';
import { useValidateUserPaymentDetails } from '../../../../../../../../../api';
import { UserPaymentDetailsValidationResult } from '../../../../../../../../../models/user-payment-details/validation/result';
import { Nullable } from '@starsoft/common/interfaces';

/**
 * UserPaymentDetailsFormContentPix Component
 * This component is responsible for rendering the form fields specific to Pix payment details.
 * It handles validation and displays appropriate error messages or success indicators.
 *
 * @param {UserPaymentDetailsFormContentFieldsProps} props - The properties for the component.
 * @param {boolean} props.isPending - Flag indicating whether the form is in a pending state.
 *
 * @returns {JSX.Element} The rendered component displaying the Pix payment details form fields.
 *
 * @example
 * // Usage example:
 * <UserPaymentDetailsFormContentPix isPending={false} />
 */
export default function UserPaymentDetailsFormContentPix({
  isPending,
}: UserPaymentDetailsFormContentFieldsProps) {
  // Hook to get the translation function for the 'common' namespace
  const { t } = useTranslation('common');

  // Destructure necessary methods and state from the form context
  const {
    watch,
    register,
    formState: { dirtyFields },
    setValue,
    setError,
    clearErrors,
  } = useFormContext<
    CreateUserPaymentDetailsInputDto & UpdateUserPaymentDetailsInputDto
  >();

  // Get the coalesced user payment type ID, which can be null
  const typeId: Nullable<string> = useCoallescedUserPaymentTypeId();

  // Watch the 'pix.key' field for changes
  const pixKey: string = watch('pix.key');

  // Use the custom hook to validate user payment details
  const {
    isPending: isPendingValidation,
    validateUserPaymentDetails,
    errorMapping,
  } = useValidateUserPaymentDetails({
    onSuccess: (validationResult: UserPaymentDetailsValidationResult) => {
      clearErrors();
      if (validationResult?.errors?.length > 0) {
        setError('pix.key', {
          type: 'validate',
        });
      }

      if (validationResult?.pix?.maskedKey) {
        setValue('pix.key', validationResult.pix.maskedKey as never);
      }
    },
  });

  // Get any errors related to the Pix key
  const pixErrors = errorMapping.get(UserPaymentDetailsFormFields.PixKey);

  // Determine if there is an error with the Pix key
  const isError: boolean =
    pixErrors?.length > 0 && !isPendingValidation && dirtyFields?.pix?.key;

  // Determine if the Pix key lookup is valid
  const isValidLookup: boolean = pixErrors?.length === 0 && !isError;

  /**
   * onBlur Function
   * This function is triggered when the Pix key input loses focus.
   * It validates the user payment details using the current Pix key.
   */
  function onBlur() {
    validateUserPaymentDetails({
      typeId,
      pix: {
        key: pixKey,
      },
    });
  }

  // Render the form group and input for the Pix key
  return (
    <FormGroup
      label={t('pix_key')}
      isLoading={isPending}
      loading={{ label: true, secondary: true }}
    >
      <Input
        className={isValidLookup ? styles.container__input : ''}
        name="pix.key"
        isLoading={isPending}
        isError={isError}
        errorMessage={pixErrors}
        showSpinner={isPendingValidation}
        register={register}
        placeholder={t('pix_key')}
        rightIcon={isValidLookup ? 'fa-solid fa-circle-check' : undefined}
        onBlur={onBlur}
      />
    </FormGroup>
  );
}
